import {
  assertAuthData,
  assertRequiredNonEmptyObject,
  assertRequiredString,
  assertRequiredStringArray,
  assertRequiredObject,
  assertRequiredNumberArray,
} from './utils';
import {
  InvalidInputError,
  UnauthorizedError,
  UnknownApiError,
} from './errors';

export default function (api) {
  /**
   * @typedef {Object} UserProfile
   * @property {string} userId
   * @property {string} email
   * @property {string} name
   * @property {Array.<string>} openIds
   * @property {string} birthDate - YYYY-MM-DD
   * @property {string} mobilePhone
   * @property {string} market
   * @property {string} locale
   * @property {boolean} verified
   * @property {boolean} active
   * @property {string} inviteCode
   * @property {Array.<string>} tags
   * @property {string} zuoraId
   * @property {string} salesforceId
   */

  /**
   * @memberof API
   * @instance
   */
  const user = {
    /**
     * Retrieve user profile data.
     * Requires auth token.
     *
     * @method
     *
     * @return {Promise} promise
     * @fulfill {UserProfile}
     * @reject {AuthTokenNotSetError}
     * @reject {ArgumentError}
     * @reject {UnauthorizedError}
     * @reject {UnknownApiError}
     */
    async get() {
      assertAuthData(api.authData);

      const res = await api.request('get', `/user/v1/users/${api.authData.userId}`);
      switch (res.status) {
        case 200: {
          return res.body;
        }
        case 401: {
          throw new UnauthorizedError(res.body.message);
        }
        default: {
          throw new UnknownApiError('server returned with unhandled status code', res);
        }
      }
    },

    /**
     * Update user profile.
     * Requires auth token.
     *
     * @method
     *
     * @param {Object} payload
     * @param {string=} payload.name
     * @param {string=} payload.email
     * @param {string=} payload.mobilePhone
     * @param {string=} payload.locale
     *
     * @return {Promise} promise
     * @fulfill {{result: "updated"}}
     * @reject {ArgumentError}
     * @reject {AuthTokenNotSetError}
     * @reject {InvalidInputError}
     * @reject {UnknownApiError}
     */
    async update(payload) {
      assertRequiredNonEmptyObject(payload, 'payload');
      assertAuthData(api.authData);

      const res = await api.request('put', `/user/v1/users/${api.authData.userId}`, {
        data: payload,
      });
      switch (res.status) {
        case 200: {
          return res.body;
        }
        case 400: {
          const fields = res.body.errors.reduce((acc, error) => ({
            ...acc,
            [error.field]: error.reason,
          }), {});
          throw new InvalidInputError(res.body.message, fields);
        }
        default: {
          throw new UnknownApiError('server returned with unhandled status code', res);
        }
      }
    },

    /**
     * Adds user tags.
     * Requires auth token.
     *
     * @method
     *
     * @param {object} payload
     * @param {Array.<string>} payload.tags
     *
     * @return {Promise} promise
     * @fulfill {undefined}
     * @reject {ArgumentError}
     * @reject {AuthTokenNotSetError}
     * @reject {InvalidInputError}
     * @reject {UnauthorizedError}
     * @reject {UnknownApiError}
     */
    async addTags({ tags }) {
      assertAuthData(api.authData);
      assertRequiredStringArray(tags, 'tags');

      const res = await api.request('post', `/user/v1/users/${api.authData.userId}/tags`, {
        data: tags,
      });
      switch (res.status) {
        case 204: {
          return undefined;
        }
        case 400: {
          throw new InvalidInputError(res.body.message);
        }
        case 401: {
          throw new UnauthorizedError(res.body.message, res);
        }
        default: {
          throw new UnknownApiError('server returned with unhandled status code', res);
        }
      }
    },

    /**
     * Gets user tags.
     * Requires auth token.
     *
     * @method
     *
     * @return {Promise} promise
     * @fulfill {Array.<string>}
     * @reject {AuthTokenNotSetError}
     * @reject {InvalidInputError}
     * @reject {UnauthorizedError}
     * @reject {UnknownApiError}
     */
    async getTags() {
      assertAuthData(api.authData);

      const res = await api.request('get', `/user/v1/users/${api.authData.userId}/tags`);
      switch (res.status) {
        case 200:
          return res.body;
        case 401:
          throw new UnauthorizedError(res.body.message);
        case 404:
          throw new InvalidInputError('Tags not found', res);
        default:
          throw new UnknownApiError('server returned with unhandled status code', res);
      }
    },

    /**
     * Removes user tag.
     * Requires auth token.
     *
     * @method
     *
     * @param {object} payload
     * @param {string} payload.tag
     *
     * @return {Promise} promise
     * @fulfill {undefined}
     * @reject {ArgumentError}
     * @reject {AuthTokenNotSetError}
     * @reject {InvalidInputError}
     * @reject {UnauthorizedError}
     * @reject {UnknownApiError}
     */
    async removeTag({ tag }) {
      assertAuthData(api.authData);
      assertRequiredString(tag, 'tag');

      const res = await api.request('delete', `/user/v1/users/${api.authData.userId}/tags/${tag}`);
      switch (res.status) {
        case 204:
          return undefined;
        case 400: {
          throw new InvalidInputError(res.body.message);
        }
        case 401: {
          throw new UnauthorizedError(res.body.message);
        }
        default:
          throw new UnknownApiError('server returned with unhandled status code', res);
      }
    },

    /**
     * Update personalization.
     * Requires auth token.
     *
     * @method
     *
     * @param {Object} payload
     * @param {Array.<string>} payload.sortedChannels
     *
     * @return {Promise} promise
     * @reject {ArgumentError}
     * @reject {AuthTokenNotSetError}
     * @reject {InvalidInputError}
     * @reject {UnknownApiError}
     */
    async personalization(payload) {
      assertAuthData(api.authData);
      assertRequiredObject(payload, 'payload');
      assertRequiredNumberArray(payload.sortedChannels, 'payload.sortedChannels');

      const res = await api.request('put', `/superscription/v1/users/${api.authData.userId}/personalisation`, {
        data: payload,
      });
      switch (res.status) {
        case 200: {
          return res.body;
        }
        case 400: {
          const fields = res.body.errors.reduce((acc, error) => ({
            ...acc,
            [error.field]: error.reason,
          }), {});
          throw new InvalidInputError(res.body.message, fields);
        }
        default: {
          throw new UnknownApiError('server returned with unhandled status code', res);
        }
      }
    },

    /**
     * Reset personalization.
     * Requires auth token.
     *
     * @method
     *
     * @return {Promise} promise
     * @reject {UnauthorizedError}
     * @reject {UnknownApiError}
     */
    async resetPersonalization() {
      assertAuthData(api.authData);

      const res = await api.request('delete', `/superscription/v1/users/${api.authData.userId}/personalisation`);

      switch (res.status) {
        case 204: {
          return res.body;
        }
        case 401: {
          throw new UnauthorizedError(res.body.message);
        }
        default: {
          throw new UnknownApiError(undefined, res);
        }
      }
    },
  };

  return user;
}
